/**
 * Sticky Nav Dropdown + Scroll Spy Alpine Component
 *
 * Below lg: dropdown trigger showing the active section label.
 * At lg+: horizontal scrolling link bar with scroll fades.
 * Both layouts share the same scroll-spy logic.
 */
export default function StickyNavDropdown() {
  return {
    stuck: false,
    open: false,
    activeLabel: '',
    canScrollLeft: false,
    canScrollRight: false,

    observer: null,
    stickyObserver: null,
    sentinel: null,
    _resizeObserver: null,

    init() {
      this.initStickyObserver();
      this.initScrollSpy();
      this.initScrollFades();
    },

    toggle() {
      this.open = !this.open;
    },

    close() {
      this.open = false;
    },

    navigate(event) {
      this.open = false;
      const href = event.currentTarget.getAttribute('href');
      if (href) {
        const target = document.querySelector(href);
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },

    initScrollFades() {
      this.$nextTick(() => this.updateScrollFades());

      if (this.$refs.scrollContainer) {
        this._resizeObserver = new ResizeObserver(() =>
          this.updateScrollFades(),
        );
        this._resizeObserver.observe(this.$refs.scrollContainer);
      }
    },

    updateScrollFades() {
      const el = this.$refs.scrollContainer;
      if (!el) return;
      const tolerance = 2;
      this.canScrollLeft = el.scrollLeft > tolerance;
      this.canScrollRight =
        el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance;
    },

    initStickyObserver() {
      const computedStyle = window.getComputedStyle(this.$el);
      const stickyTop = computedStyle.top || '0px';
      const stickyTopValue = parseInt(stickyTop);

      const navHeight = this.$el.offsetHeight;
      document.documentElement.style.setProperty(
        '--spacing-nav',
        `${navHeight}px`,
      );

      this.sentinel = document.createElement('div');
      Object.assign(this.sentinel.style, {
        height: '1px',
        width: '100%',
        pointerEvents: 'none',
        visibility: 'hidden',
      });
      this.sentinel.setAttribute('aria-hidden', 'true');

      this.$el.parentNode.insertBefore(this.sentinel, this.$el);

      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.stuck = !entry.isIntersecting;
        },
        {
          threshold: 0,
          rootMargin: `-${stickyTopValue}px 0px 0px 0px`,
        },
      );

      this.stickyObserver.observe(this.sentinel);
    },

    initScrollSpy() {
      const navLinks = this.$el.querySelectorAll('.scroll-spy-link');

      // Group all links by section ID so both layouts get active states
      const sectionMap = {};
      navLinks.forEach((link) => {
        const sectionId = link.getAttribute('data-scroll-spy');
        if (sectionId) {
          if (!sectionMap[sectionId]) sectionMap[sectionId] = [];
          sectionMap[sectionId].push(link);
        }
      });

      if (Object.keys(sectionMap).length === 0) return;

      const firstLink = navLinks[0];
      if (firstLink) {
        this.activeLabel = firstLink.textContent.trim();
      }

      const options = {
        root: null,
        rootMargin: '-10% 0px -85% 0px',
        threshold: 0,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const links = sectionMap[entry.target.id];

            if (links) {
              navLinks.forEach((l) => l.classList.remove('active'));
              links.forEach((l) => l.classList.add('active'));
              this.activeLabel = links[0].textContent.trim();
              this.scrollActiveIntoView(links);
            }
          }
        });
      }, options);

      Object.keys(sectionMap).forEach((id) => {
        const section = document.getElementById(id);
        if (section) {
          this.observer.observe(section);
        }
      });
    },

    scrollActiveIntoView(links) {
      const container = this.$refs.scrollContainer;
      if (!container || container.scrollWidth <= container.clientWidth) return;

      // Find the link inside the horizontal nav (not the dropdown)
      const link = links.find((l) => container.contains(l));
      if (!link) return;

      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = requestAnimationFrame(() => {
        const linkRect = link.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTarget =
          container.scrollLeft +
          (linkRect.left - containerRect.left) -
          containerRect.width / 2 +
          linkRect.width / 2;

        container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      });
    },

    destroy() {
      if (this.observer) this.observer.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this.sentinel?.parentNode)
        this.sentinel.parentNode.removeChild(this.sentinel);
    },
  };
}
